import React, {useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {UseFormReturn} from 'react-hook-form';
import ReactHookFormInputFieldWithCounter from '../../../../components/reactHookFormComponents/ReactHookFormInputFieldWithCounter';
import ReactHookFormDateTimeField from '../../../../components/reactHookFormComponents/ReactHookFormDateTimeField';
import {presentationOptionsWithSelected} from '../../utils/mappers';
import {useFetchPresentations} from '../../queries';
import {SORT_DEFAULT} from '../../../../../constants';
import ReactHookFormSelect from '../../../../components/reactHookFormComponents/ReactHookFormSelect';
import {IPresentation} from '../../../../../incase-domain/presentation';

interface EditRoomGeneralTabProps {
    useForm: UseFormReturn;
    selectedPresentation?: IPresentation;
}

function EditRoomGeneralTab({useForm, selectedPresentation}: EditRoomGeneralTabProps): JSX.Element {
    const {t} = useTranslation();

    const {control, formState, watch} = useForm;
    const {errors} = formState;

    const [presentationSearchTerm, setPresentationSearchTerm] = useState<string>('');

    const {
        data: presentationData,
        hasNextPage,
        fetchNextPage,
    } = useFetchPresentations({
        searchTerm: presentationSearchTerm,
        sortBy: SORT_DEFAULT,
    });

    const options = useMemo(
        () => presentationOptionsWithSelected(presentationData, selectedPresentation),
        [presentationData, selectedPresentation]
    );

    const handleScrollToBottom = (): void => {
        if (hasNextPage) fetchNextPage();
    };

    return (
        <div>
            <div className="c-editRoomModal__content_header">{t('roomsPage.general')}</div>

            <div className="u-mt-2" data-cy="room_name">
                <ReactHookFormInputFieldWithCounter
                    text={`${t('roomsPage.modals.roomName')}`}
                    fieldName="roomName"
                    errors={errors}
                    control={control}
                    maxLength={40}
                    className="c-sidepanel__input"
                    textClassName="c-room_modal__input__text"
                />
            </div>

            <div className="u-flex u-h-between u-v-center c-room_modal__input__text">
                <div>{`${t('roomsPage.createRoomModal.presentation')}`}</div>
            </div>

            <ReactHookFormSelect
                fieldName="presentationId"
                options={options}
                useForm={useForm}
                setSearchTerm={setPresentationSearchTerm}
                placeholder={`${t('roomsPage.modals.selectPresentation')}`}
                onScrollToBottom={handleScrollToBottom}
            />

            <div className="u-flex u-flex-col u-gap-2 u-mt-2">
                <div className="c-room_modal__input__text">{t('roomsPage.modals.schedule')}</div>
                <ReactHookFormDateTimeField
                    text={`${t('roomsPage.modals.startTime')}`}
                    fieldName="startTime"
                    control={control}
                    errors={errors}
                    textClassName="c-room_modal__input__text"
                    dataCy="room_start_time"
                />
                <ReactHookFormDateTimeField
                    text={`${t('roomsPage.modals.endTime')}`}
                    fieldName="endTime"
                    control={control}
                    errors={errors}
                    textClassName="c-room_modal__input__text"
                    minDateTime={watch('startTime')}
                    dataCy="room_end_time"
                />
            </div>
        </div>
    );
}

export default EditRoomGeneralTab;
